import { useEffect, useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import { Form, useRefresh } from "ra-core";
import { Link } from "react-router";
import { Alert, AlertDescription } from "@/components/ds/ui/alert";
import { Button } from "@/components/ds/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ds/ui/dialog";
import { FormToolbar } from "@/components/ds/admin/simple-form";
import { FileInput } from "@/components/ds/admin/file-input";
import { FileField } from "@/components/ds/admin/file-field";
import { usePapaParse } from "../misc/usePapaParse";
import type { ActivityImportSchema } from "./useActivityImport";
import { useActivityImport } from "./useActivityImport";

const SAMPLE_CSV = `name,amount,stage,company_name,expected_closing_date\n5G Tower FMPS,25000,opportunity,RANext,2026-10-31`;

export const ActivityImportButton = () => {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
  onClick={() => setModalOpen(true)}
  className="flex items-center gap-2"
  >
  <Upload className="h-4 w-4" /> Import Activities
  </Button>
  <DealImportDialog open={modalOpen} onClose={() => setModalOpen(false)} />
  </>
);
};

function DealImportDialog({
                            open,
                            onClose,
                          }: {
  open: boolean;
  onClose: () => void;
}) {
  const refresh = useRefresh();
  const processBatch = useActivityImport();
  const { importer, parseCsv, reset } = usePapaParse<ActivityImportSchema>({
    batchSize: 10,
    processBatch,
  });
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (importer.state === "complete") refresh();
  }, [importer.state, refresh]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
  <DialogContent className="max-w-2xl">
  <Form className="flex flex-col gap-4">
    <DialogHeader>
      <DialogTitle>Import Activities</DialogTitle>
  <DialogDescription>
  Upload a Activities CSV. Ensure the "company_name" matches an existing
  company.
  </DialogDescription>
  </DialogHeader>

  <div className="flex flex-col space-y-3">
    {importer.state === "running" && (
        <Alert>
          <AlertDescription className="flex flex-row gap-4">
        <Loader2 className="h-5 w-5 animate-spin" />
          Importing {importer.importCount} / {importer.rowCount}...
  </AlertDescription>
  </Alert>
)}

  {importer.state === "complete" && (
    <Alert>
      <AlertDescription>
                  ✅ Imported {importer.importCount} deals (
    {importer.errorCount} errors).
    </AlertDescription>
    </Alert>
  )}

  {importer.state === "idle" && (
    <>
      <Alert>
        <AlertDescription>
          <Button asChild variant="outline" size="sm">
  <Link
    to={`data:text/csv;name=sample.csv;charset=utf-8,${encodeURIComponent(SAMPLE_CSV)}`}
    download="deals_sample.csv"
      >
      Download sample CSV
  </Link>
  </Button>
  </AlertDescription>
  </Alert>
  <FileInput
    source="csv"
    label="Deals CSV"
    accept={{ "text/csv": [".csv"] }}
    onChange={setFile}
    >
    <FileField source="src" title="title" target="_blank" />
    </FileInput>
    </>
  )}
  </div>
  </Form>

  <div className="flex justify-end pt-4">
    <FormToolbar>
      {importer.state === "idle" ? (
          <Button onClick={() => file && parseCsv(file)} disabled={!file}>
  Import
  </Button>
) : (
    <Button
      variant="outline"
  onClick={() => {
    reset();
    onClose();
  }}
  disabled={importer.state === "running"}
    >
    Close
    </Button>
)}
  </FormToolbar>
  </div>
  </DialogContent>
  </Dialog>
);
}
